if(!customElements.get("carousel-component")) {
  class Carousel extends HTMLElement {
    constructor() {
      super();
      this.carouselElement = this;
  
      // Defer non-critical data attribute parsing
      this.initDataAttributes();
      
      // Carousel element should have class splide
      if (!this.carouselElement.classList.contains('splide')) return;

      // Use requestIdleCallback for non-critical initialization
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => this.initCarousel());
      } else {
        setTimeout(() => this.initCarousel(), 0);
      }
    }

    initDataAttributes() {
      this.desktopPerPage = this.carouselElement.dataset['desktopperpage'] || 4;
      this.mobilePerPage = this.carouselElement.dataset['mobileperpage'] || 1;
      this.tabletPerPage = this.carouselElement.dataset['tabletperpage'];
      this.focus = this.carouselElement.dataset['focus'];
      this.mobileFocus = this.carouselElement.dataset['mobilefocus'];
      this.type = this.carouselElement.dataset['type'];
      this.mobileType = this.carouselElement.dataset['mobiletype'];
      this.gap = this.carouselElement.dataset['gapbetweenslides'] || '2.4rem';
      this.gapMobile = this.carouselElement.dataset['gapbetweenslidesonmobile'] || 0;
      this.autoplaySpeed = parseInt(this.dataset['autoplaySpeed']) || 3000;
      this.direction = this.carouselElement.dataset['direction'] || 'ltr';
      this.mobilePaddingLeft = this.carouselElement.dataset['mobilepaddingleft'] || '0';
      this.mobilePaddingRight = this.carouselElement.dataset['mobilepaddingright'] || '0';
      this.desktopPaddingLeft = this.carouselElement.dataset['desktoppaddingleft'] || '0';
      this.desktopPaddingRight = this.carouselElement.dataset['desktoppaddingright'] || '0';
      this.smallMobile = this.carouselElement.dataset['smallmobile'] === 'true' || false;
      this.showarrows = this.carouselElement.dataset['showarrows'] === 'true' || false;
      this.autoplay = this.carouselElement.dataset['autoplay'] === 'true' || false;
      this.showarrowsonmobile = this.carouselElement.dataset['showarrowsonmobile'] === 'true' || false;
      this.showdots = this.carouselElement.dataset['showdots'] === 'true' || false;
      this.showdotsonmobile = this.carouselElement.dataset['showdotsonmobile'] === 'true' || false;
      this.isNavigation = this.carouselElement.dataset['isnavigation'] === 'true' || false;
      this.disableDrag = this.carouselElement.dataset['disabledrag'] === 'true' || false;
      this.destroyOnMobile = this.carouselElement.dataset['enableonmobile'] == 'false' || false;
      this.destroyOnDesktop = this.carouselElement.dataset['enableondesktop'] == 'false' || false;
      this.omitEnd = this.carouselElement.dataset['omitEnd'] == 'false' || false;
      this.refresh = this.carouselElement.dataset['refresh'] == 'true' || false;
      this.sync = this.carouselElement.dataset['carouselsyncselector'] || false;
    }
   
    initCarousel() {
      if (this.destroyOnMobile && this.destroyOnDesktop) return;
    
      const breakpoints = {
        750: {
          ...(this.type ? { type: this.mobileType } : {}),
          ...(this.focus ? { focus: this.mobileFocus } : {}),
          padding: { left: this.mobilePaddingLeft, right: this.mobilePaddingRight },
          perPage: this.mobilePerPage,
          arrows: this.showarrowsonmobile,
          pagination: this.showdotsonmobile,
          gap: this.gapMobile,
          destroy: this.destroyOnMobile,
        },
        ...(this.tabletPerPage && {
          1024: {
            perPage: this.tabletPerPage,
            gap: this.gap,
          },
        }),
        ...(this.smallMobile && {
          375: {
            perPage: this.mobilePerPage,
            gap: this.gapMobile,
            padding: {
              right: this.mobilePaddingRight
            }
          },
        }),
      };
    
      // Initialize Splide with optimized options
      this.carousel = new Splide(this.carouselElement, {
        perPage: this.desktopPerPage,
        ...(this.type ? { type: this.type } : {}),
        ...(this.focus ? { focus: this.focus } : {}),
        autoplay: this.autoplay,
        interval: this.autoplaySpeed,
        gap: this.gap,
        arrows: this.showarrows,
        pagination: this.showdots,
        isNavigation: this.isNavigation,
        drag: !this.disableDrag,
        direction: this.direction,
        destroy: this.destroyOnDesktop,
        padding: { left: this.desktopPaddingLeft, right: this.desktopPaddingRight },
        omitEnd: this.omitEnd,
        breakpoints: breakpoints,
        // Add performance optimizations
        waitForTransition: false,
        updateOnMove: false,
        trimSpace: true,
        preloadPages: 1,
        preloadImages: false,
        lazyLoad: 'nearby'
      });
    
      if (this.sync) {
        this.initCarouselSync();
      } else {
        this.initProgressBar(this.carousel);
        this.carousel.mount();
        
        if (this.refresh) {
          // Defer refresh to next frame
          requestAnimationFrame(() => this.carousel.refresh());
        }
      }
    }
    
    initProgressBar(skeletonSplide) {
      const bar = this.querySelector('.my-slider-progress-bar');
      if(!bar) return;

      this.carousel.on('mounted move', () => {
        const end = skeletonSplide.Components.Controller.getEnd() + 1;
        const rate = Math.min((skeletonSplide.index + 1) / end, 1);
        bar.style.width = `${100 * rate}%`;
      });
    }
  
    initCarouselSync() {
      this.syncElement = document.querySelector(this.sync);
      if (!this.syncElement) return;

      this.direction = this.syncElement.dataset['direction'] || 'ltr';
      this.sliderTab = this.syncElement.dataset['slidertab'] === 'true' || false;
  
      this.carouselSync = new Splide(this.sync, {
        updateOnMove: true,
        perPage: this.syncElement.dataset['desktopperpage'] || 5,
        type: this.syncElement.dataset['type'] || 'loop',
        focus: this.syncElement.dataset['focus'] || 'left',
        isNavigation: this.syncElement.dataset['isnavigation'] === 'true' || false,
        pagination: false,
        gap: this.syncElement.dataset['desktopslidegap'] || '0rem',
        arrows: this.syncElement.dataset['showarrows'] === 'true' || false,
        drag: true,
        preloadPages: 1,
        preloadImages: false,
        lazyLoad: 'nearby',
        ...(this.direction === 'ttb' && {
          height: this.getPrimarySlideFirstImageHeight(),
          direction: 'ttb',
          autoHeight: true,
        }),
        breakpoints: {
          767: {
            height: 'auto',
            direction: 'ltr',
            gap: this.gapMobile
          }
        },
      });

      if(this.sliderTab) {
        this.carousel.on('move', (newIndex) => {
          requestAnimationFrame(() => {
            document.querySelectorAll('.content__block').forEach((block, index) => {
              const isActive = index === newIndex;
              block.classList.toggle('visible', isActive);
              block.style.transform = isActive ? 'translateX(0%)' : 'translateX(100%)';
              block.classList.toggle('hidden', !isActive);
            });
          });
        });
      }
  
      this.carousel.sync(this.carouselSync);
      this.carousel.mount();
      this.carouselSync.mount();
    }
  
    refreshSlider() {
      requestAnimationFrame(() => {
        this.carousel.refresh();
        if (this.carouselSync) {
          this.carouselSync.refresh();
        }
      });
    }

    getPrimarySlideFirstImageHeight() {
      const image = this.querySelector('.splide__list img');
      if (!image) return 'auto';
      
      const containerWidth = this.querySelector('.splide__list').clientWidth;
      const originalWidth = image.getAttribute('width');
      const originalHeight = image.getAttribute('height');
  
      return originalWidth && originalHeight ? 
        (containerWidth / originalWidth) * originalHeight : 'auto';
    }
  }

  window.Carousel = Carousel;
  customElements.define('carousel-component', Carousel);
}